import { Empty, Select, Spin } from "antd";
import { useEffect, useRef, useState } from "react";
import { getDataLockOrderFieldOptions } from "../../services/domains/data-lock/service";
import type { DataLockPaginatedEnvelope } from "../../services/domains/data-lock/types";
import styles from "./DataLock.module.scss";
import type { DataLockFormOption } from "./types";

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;
const ALL_SENTINEL = "__DATA_LOCK_ALL_OPTION__";
const SCROLL_THRESHOLD_PX = 24;

type Query = {
  fieldCode: string;
  search: string;
  revision: number;
};

type RequestMode = "replace" | "append";

export type OrderFieldValueSelectProps = {
  fieldCode: string;
  allOption: DataLockFormOption;
  allValues: boolean;
  values: string[];
  selectedOptions: DataLockFormOption[];
  disabled?: boolean;
  placeholder?: string;
  onChange: (next: {
    allValues: boolean;
    values: string[];
    selectedOptions: DataLockFormOption[];
  }) => void;
};

const mergeUniqueOptions = (
  existing: DataLockFormOption[],
  incoming: DataLockFormOption[],
): DataLockFormOption[] => {
  const seen = new Set(existing.map((option) => option.value));
  return [
    ...existing,
    ...incoming.filter((option) => {
      if (seen.has(option.value)) return false;
      seen.add(option.value);
      return true;
    }),
  ];
};

const requestKey = (query: Query, page: number) =>
  JSON.stringify([query.fieldCode, query.search, page]);

export const OrderFieldValueSelect = ({
  fieldCode,
  allOption,
  allValues,
  values,
  selectedOptions,
  disabled,
  placeholder,
  onChange,
}: OrderFieldValueSelectProps) => {
  const [searchState, setSearchState] = useState({
    fieldCode,
    text: "",
    revision: 0,
  });
  const [activeQuery, setActiveQuery] = useState<Query>({
    fieldCode,
    search: "",
    revision: 0,
  });
  const [remoteOptions, setRemoteOptions] = useState<DataLockFormOption[]>([]);
  const [current, setCurrent] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mountedRef = useRef(false);
  const previousFieldRef = useRef(fieldCode);
  const queryRevisionRef = useRef(0);
  const requestGenerationRef = useRef(0);
  const inFlightRequestsRef = useRef(
    new Map<
      string,
      Promise<DataLockPaginatedEnvelope<DataLockFormOption>>
    >(),
  );
  const subscriptionsRef = useRef(new Set<string>());
  const requestPageRef = useRef<
    (
      query: Query,
      page: number,
      mode: RequestMode,
      generation: number,
    ) => void
  >(() => undefined);

  const searchText =
    searchState.fieldCode === fieldCode ? searchState.text : "";
  const queryPending =
    activeQuery.fieldCode !== fieldCode ||
    searchState.fieldCode !== fieldCode ||
    activeQuery.search !== searchText ||
    activeQuery.revision !== searchState.revision;

  requestPageRef.current = (query, page, mode, generation) => {
    const key = requestKey(query, page);
    const subscriptionKey = `${generation}:${key}`;
    if (subscriptionsRef.current.has(subscriptionKey)) return;

    let request = inFlightRequestsRef.current.get(key);
    if (!request) {
      request = getDataLockOrderFieldOptions(query.fieldCode, {
        current: page,
        pageSize: PAGE_SIZE,
        search: query.search,
      });
      inFlightRequestsRef.current.set(key, request);
    }

    subscriptionsRef.current.add(subscriptionKey);
    setLoading(true);
    setError(null);

    request
      .then((result) => {
        if (
          !mountedRef.current ||
          generation !== requestGenerationRef.current
        ) {
          return;
        }

        setRemoteOptions((existing) =>
          mode === "replace"
            ? mergeUniqueOptions([], result.data)
            : mergeUniqueOptions(existing, result.data),
        );
        setCurrent(result.current);
        setTotalPages(result.totalPages);
      })
      .catch(() => {
        if (
          mountedRef.current &&
          generation === requestGenerationRef.current
        ) {
          setError("Không thể tải dữ liệu. Hãy tìm kiếm hoặc mở lại để thử.");
        }
      })
      .finally(() => {
        subscriptionsRef.current.delete(subscriptionKey);
        if (inFlightRequestsRef.current.get(key) === request) {
          inFlightRequestsRef.current.delete(key);
        }
        if (
          mountedRef.current &&
          generation === requestGenerationRef.current
        ) {
          setLoading(false);
        }
      });
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestGenerationRef.current += 1;
    };
  }, []);

  useEffect(() => {
    if (previousFieldRef.current === fieldCode) return;

    previousFieldRef.current = fieldCode;
    requestGenerationRef.current += 1;
    const revision = queryRevisionRef.current + 1;
    queryRevisionRef.current = revision;
    setSearchState({ fieldCode, text: "", revision });
    setActiveQuery({ fieldCode, search: "", revision });
    setRemoteOptions([]);
    setCurrent(0);
    setTotalPages(1);
    setLoading(false);
    setError(null);
  }, [fieldCode]);

  useEffect(() => {
    if (
      activeQuery.fieldCode === fieldCode &&
      activeQuery.search === searchText &&
      activeQuery.revision === searchState.revision
    ) {
      return;
    }

    const timer = window.setTimeout(() => {
      setActiveQuery({
        fieldCode,
        search: searchText,
        revision: searchState.revision,
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [
    activeQuery.fieldCode,
    activeQuery.revision,
    activeQuery.search,
    fieldCode,
    searchState.revision,
    searchText,
  ]);

  useEffect(() => {
    const generation = requestGenerationRef.current + 1;
    requestGenerationRef.current = generation;
    setRemoteOptions([]);
    setCurrent(0);
    setTotalPages(1);
    setError(null);
    requestPageRef.current(activeQuery, 1, "replace", generation);
  }, [activeQuery.fieldCode, activeQuery.revision, activeQuery.search]);

  const specificSelectedOptions = selectedOptions.filter(
    (option) =>
      option.value !== ALL_SENTINEL && option.value !== allOption.value,
  );
  const displayedSpecificOptions = mergeUniqueOptions(
    specificSelectedOptions,
    remoteOptions.filter(
      (option) =>
        option.value !== ALL_SENTINEL && option.value !== allOption.value,
    ),
  );
  const options = [
    { value: ALL_SENTINEL, label: allOption.label },
    ...displayedSpecificOptions,
  ];
  const optionByValue = new Map(
    displayedSpecificOptions.map((option) => [option.value, option]),
  );
  const controlledValues = allValues
    ? [ALL_SENTINEL]
    : values.filter(
        (value) => value !== ALL_SENTINEL && value !== allOption.value,
      );

  const handleSearch = (nextSearch: string) => {
    if (nextSearch === searchText) return;

    requestGenerationRef.current += 1;
    const revision = queryRevisionRef.current + 1;
    queryRevisionRef.current = revision;
    setSearchState({ fieldCode, text: nextSearch, revision });
    setRemoteOptions([]);
    setCurrent(0);
    setTotalPages(1);
    setLoading(false);
    setError(null);
  };

  const handleChange = (nextValues: string[]) => {
    if (searchText) handleSearch("");

    if (nextValues.includes(ALL_SENTINEL)) {
      onChange({ allValues: true, values: [], selectedOptions: [] });
      return;
    }

    const nextSpecificValues = Array.from(
      new Set(
        nextValues.filter(
          (value) => value !== ALL_SENTINEL && value !== allOption.value,
        ),
      ),
    );
    onChange({
      allValues: false,
      values: nextSpecificValues,
      selectedOptions: nextSpecificValues.map(
        (value) => optionByValue.get(value) ?? { value, label: value },
      ),
    });
  };

  const handlePopupScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const isNearBottom =
      target.scrollTop + target.clientHeight >=
      target.scrollHeight - SCROLL_THRESHOLD_PX;
    if (
      !isNearBottom ||
      loading ||
      current < 1 ||
      current >= totalPages ||
      activeQuery.fieldCode !== fieldCode ||
      activeQuery.search !== searchText ||
      activeQuery.revision !== searchState.revision
    ) {
      return;
    }

    requestPageRef.current(
      activeQuery,
      current + 1,
      "append",
      requestGenerationRef.current,
    );
  };

  const handleDropdownVisibleChange = (open: boolean) => {
    if (!open || !error) return;

    const nextPage = current > 0 && current < totalPages ? current + 1 : 1;
    requestPageRef.current(
      activeQuery,
      nextPage,
      nextPage === 1 ? "replace" : "append",
      requestGenerationRef.current,
    );
  };

  const popupStatus = loading || queryPending ? (
    <Spin size="small" />
  ) : error ? (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={error} />
  ) : remoteOptions.length === 0 ? (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không có dữ liệu" />
  ) : (
    null
  );

  return (
    <Select<string[], DataLockFormOption>
      className={styles.Control}
      mode="multiple"
      value={controlledValues}
      options={options}
      disabled={disabled}
      placeholder={placeholder}
      loading={loading || queryPending}
      showSearch
      filterOption={false}
      searchValue={searchText}
      popupRender={(menu) => (
        <>
          {menu}
          {popupStatus ? (
            <div style={{ padding: 8, textAlign: "center" }}>{popupStatus}</div>
          ) : null}
        </>
      )}
      onSearch={handleSearch}
      onChange={handleChange}
      onPopupScroll={handlePopupScroll}
      onOpenChange={handleDropdownVisibleChange}
    />
  );
};

export default OrderFieldValueSelect;
